import { onBeforeUnmount, onDeactivated, unref, watch } from 'vue';
import { useRoute } from 'vue-router';

/**
 * Hide a PrimeVue Popover/Menu when the keep-alive page is left.
 * Overlays are teleported to document.body, so they stay visible otherwise.
 *
 * @param {import('vue').Ref<{ hide?: () => void }|null>} overlayRef
 */
export function useHideOverlayOnPageLeave(overlayRef) {
  const hide = () => {
    unref(overlayRef)?.hide?.();
  };

  onDeactivated(hide);
  onBeforeUnmount(hide);
}

/**
 * Run a closer when the keep-alive page is left (drawers, dialogs, inline panels).
 *
 * @param {() => void} closeFn
 */
export function useCloseOnPageLeave(closeFn) {
  onDeactivated(closeFn);
  onBeforeUnmount(closeFn);
}

/**
 * Close teleported PrimeVue overlays (Select, DatePicker, Popover, Menu).
 * Sidebar uses @click.stop, so their outside-click listeners never see nav clicks.
 */
export function dismissPrimeOverlays() {
  if (typeof document === 'undefined') return;
  const opts = { bubbles: true, cancelable: true };
  document.dispatchEvent(new MouseEvent('mousedown', opts));
  document.dispatchEvent(new MouseEvent('click', opts));
}

/** Dismiss overlays on page switches, not on query-only updates (tabs/filters). */
export function useDismissPrimeOverlaysOnRouteChange() {
  const route = useRoute();
  watch(
    () => `${String(route.name || '')}:${route.path}`,
    () => {
      dismissPrimeOverlays();
    }
  );
}
